/**
 * DiscusionInterface.jsx — Interface principale de messagerie
 *
 * Affiche la liste de toutes les discussions de l'utilisateur connecté
 * dans un panneau gauche, et la conversation sélectionnée dans le panneau droit.
 * L'API est interrogée différemment selon le rôle :
 *  - admin   → toutes les discussions (?all=1)
 *  - agency  → les discussions liées à l'agence (?agencyId=...)
 *  - user    → les discussions du client (?customerId=...)
 *
 * Socket.io met à jour la conversation en temps réel (event new_message).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getSocket } from '../../utils/socket.js';
import { FiArrowLeft, FiCamera, FiCheck, FiChevronDown, FiChevronUp, FiClock, FiMessageCircle, FiMoreVertical, FiRefreshCw, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import NotificationCenter from '../notification/Notification.jsx';
import BottomNav from '../shared/BottomNav.jsx';
import { notifyAuthUserChanged } from '../../utils/authEvents';
import '../home/Home.css';
import './ChatPage.css';
import './DiscusionInterface.css';

const apiBaseUrl = getApiBaseUrl();

/** Palette d'avatars colorés dérivée du premier caractère du nom */
const AVATAR_PALETTE = [
	{ bg: '#dbeafe', color: '#1d4ed8' },
	{ bg: '#d1fae5', color: '#065f46' },
	{ bg: '#fce7f3', color: '#9d174d' },
	{ bg: '#fef3c7', color: '#92400e' },
	{ bg: '#ede9fe', color: '#5b21b6' },
	{ bg: '#fee2e2', color: '#991b1b' },
	{ bg: '#e0f2fe', color: '#075985' },
	{ bg: '#fdf4ff', color: '#7e22ce' },
];
function getAvatarStyle(name) {
	const idx = ((name || 'A').charCodeAt(0) + (name || 'A').length) % AVATAR_PALETTE.length;
	return AVATAR_PALETTE[idx];
}

/** Formate une date en format court lisible (ex: "12/05 14:30") */
function formatDateTime(value) {
	if (!value) return '--';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '--';
	return date.toLocaleString('fr-FR', {
		day: '2-digit', month: '2-digit',
		hour: '2-digit', minute: '2-digit'
	});
}

/** Lit et normalise l'utilisateur connecté depuis localStorage */
function getStoredUser() {
	try {
		const authRaw = window.localStorage.getItem('exp-auth-user');
		const profileRaw = window.localStorage.getItem('exp-user-profile');
		const authUser = authRaw ? JSON.parse(authRaw) : null;
		const profileUser = profileRaw ? JSON.parse(profileRaw) : null;
		const user = authUser || profileUser || null;
		if (!user) return null;

		const firstName = user.firstName || user.prenom || '';
		const lastName = user.lastName || user.nom || '';
		const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';

		return {
			id: user.id || user._id || null,
			name: [firstName, lastName].filter(Boolean).join(' ').trim() || user.email || 'Client',
			role: user.role || 'user',
			email: String(user.email || '').trim().toLowerCase(),
			photoUrl: user.profilePhotoUrl || '',
			initials
		};
	} catch {
		return null;
	}
}

function DiscusionInterface() {
	const navigate = useNavigate();
	const location = useLocation();
	const bottomRef = useRef(null);

	// Utilisateur connecté (ne change pas pendant la session)
	const [authUser] = useState(() => getStoredUser());

	// ── État des discussions ──────────────────────────────────────────────────
	const [discussions, setDiscussions] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');

	// ── État navigation profil ────────────────────────────────────────────────
	const [profileOpen, setProfileOpen] = useState(false);

	// ── État conversation sélectionnée ────────────────────────────────────────
	const [selectedDiscussion, setSelectedDiscussion] = useState(null);
	const [conversation, setConversation] = useState(null);
	const [conversationLoading, setConversationLoading] = useState(false);
	const [conversationError, setConversationError] = useState('');

	// ── État compositeur de message ───────────────────────────────────────────
	const [draft, setDraft] = useState('');
	const [sending, setSending] = useState(false);
	const [refreshToken, setRefreshToken] = useState(0); // force le rechargement

	// ── Suppression discussions ───────────────────────────────────────────────
	const [openMenuId, setOpenMenuId] = useState(null);
	const [listExpanded, setListExpanded] = useState(false);
	const [deleteAllLoading, setDeleteAllLoading] = useState(false);

	// ── Modale de confirmation ────────────────────────────────────────────────
	const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', subtext: '', onConfirm: null });
	const showConfirm = (message, subtext, onConfirm) => setConfirmDialog({ open: true, message, subtext, onConfirm });

	// Prise en charge véhicule (photos sur place) ───────────────────────────
	const [pickupMenuOpen, setPickupMenuOpen] = useState(false); // menu choix nb photos
	const [pickupMaxPhotos, setPickupMaxPhotos] = useState(4);
	const [pickupCamOpen, setPickupCamOpen] = useState(false);
	const [pickupPhotos, setPickupPhotos] = useState([]);   // { file, previewUrl }[]
	const [pickupSending, setPickupSending] = useState(false);
	const pickupStreamRef = useRef(null);
	const pickupVideoRef = useRef(null);
	const [pickupCamError, setPickupCamError] = useState('');

	// Assigne srcObject après montage du <video> (fix bug caméra)
	useEffect(() => {
		if (pickupCamOpen && pickupVideoRef.current && pickupStreamRef.current) {
			pickupVideoRef.current.srcObject = pickupStreamRef.current;
			pickupVideoRef.current.play().catch(() => {});
		}
	}, [pickupCamOpen]);

	// Cleanup stream au démontage
	useEffect(() => () => stopPickupStream(), []);

	const stopPickupStream = () => {
		if (pickupStreamRef.current) {
			pickupStreamRef.current.getTracks().forEach(t => t.stop());
			pickupStreamRef.current = null;
		}
	};

	const openPickupCam = async () => {
		setPickupCamError('');
		stopPickupStream();
		if (!navigator.mediaDevices?.getUserMedia) {
			setPickupCamError('Caméra non disponible sur ce navigateur.');
			return;
		}
		try {
			const s = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
				audio: false,
			});
			pickupStreamRef.current = s;
			setPickupCamOpen(true);
		} catch {
			setPickupCamError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
		}
	};

	const closePickupCam = () => {
		stopPickupStream();
		setPickupCamOpen(false);
	};

	const capturePickupPhoto = () => {
		const video = pickupVideoRef.current;
		if (!video || video.readyState < 2) { setPickupCamError('Caméra pas encore prête.'); return; }
		const w = video.videoWidth || 1280;
		const h = video.videoHeight || 720;
		const canvas = document.createElement('canvas');
		canvas.width = w; canvas.height = h;
		canvas.getContext('2d').drawImage(video, 0, 0, w, h);
		canvas.toBlob((blob) => {
			if (!blob) return;
			const file = new File([blob], `pickup-${Date.now()}.jpg`, { type: 'image/jpeg' });
			const previewUrl = URL.createObjectURL(blob);
			setPickupPhotos(prev => {
					const updated = [...prev, { file, previewUrl }];
					if (updated.length < pickupMaxPhotos) {
						// Réouvre la caméra automatiquement pour la photo suivante
						setTimeout(() => openPickupCam(), 300);
					}
					return updated.slice(0, pickupMaxPhotos);
				});
			closePickupCam();
		}, 'image/jpeg', 0.92);
	};

	const confirmPickup = async () => {
		if (!conversation?._id || !authUser || pickupPhotos.length === 0) return;
		setPickupSending(true);
		try {
			// 1. Upload des photos vers Cloudinary
			let photoUrls = [];
			try {
				const formData = new FormData();
				pickupPhotos.forEach(p => formData.append('files', p.file));
				formData.append('folder', 'pickup');
				const uploadRes = await fetch(`${apiBaseUrl}/api/upload`, {
					method: 'POST',
					body: formData,
				});
				if (uploadRes.ok) {
					const uploadData = await uploadRes.json();
					photoUrls = (uploadData.files || []).map(f => f.url);
				}
			} catch (_) { /* l'échec de l'upload ne bloque pas le message */ }

			// 2. Envoi du message avec les URLs Cloudinary
			const now = new Date().toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
			const msgText = `✅ Prise en charge véhicule — ${pickupPhotos.length} photo(s) enregistrée(s).\nHeure : ${now}`;
			const res = await fetch(`${apiBaseUrl}/api/discussions/${conversation._id}/messages`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					text: msgText,
					senderRole: 'customer',
					senderName: authUser.name,
					senderId: authUser.id,
					photoUrls,
				}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.message || 'Envoi impossible.');
			setConversation(data);
			setSelectedDiscussion(data);
			// Libère les URLs blob
			pickupPhotos.forEach(p => URL.revokeObjectURL(p.previewUrl));
			setPickupPhotos([]);
		} catch (err) {
			setPickupCamError(err.message || 'Envoi impossible.');
		} finally {
			setPickupSending(false);
		}
	};

	// Photo de l'autre participant (chargée dynamiquement)
	const [otherUserPhoto, setOtherUserPhoto] = useState('');

	const userId = useMemo(() => String(authUser?.id || authUser?.name || '').trim(), [authUser?.id, authUser?.name]);
	const hasUser = Boolean(authUser);

	// Messages de la conversation sélectionnée (avec fallback sur selectedDiscussion)
	const selectedMessages = useMemo(() => conversation?.messages || selectedDiscussion?.messages || [], [conversation, selectedDiscussion]);

	/**
	 * loadDiscussions — Charge la liste des discussions selon le rôle.
	 * Trie par date du dernier message et sélectionne automatiquement
	 * la discussion demandée via navigation (ex: clic sur notification).
	 */
	const loadDiscussions = useCallback(async () => {
		if (!hasUser) return;
		setIsLoading(true);
		setError('');

		try {
			let endpoint;
			if (authUser?.role === 'agency' && userId) {
				endpoint = `${apiBaseUrl}/api/discussions?agencyId=${encodeURIComponent(userId)}`;
			} else if (authUser?.role === 'admin') {
				endpoint = `${apiBaseUrl}/api/discussions?all=1`;
			} else {
				endpoint = userId
					? `${apiBaseUrl}/api/discussions?customerId=${encodeURIComponent(userId)}`
					: `${apiBaseUrl}/api/discussions`;
			}

			const response = await fetch(endpoint);
			const data = await response.json();
			if (!response.ok) throw new Error(data.message || 'Impossible de charger les discussions.');

			const list = Array.isArray(data) ? data : [];
			// Tri : la discussion avec le message le plus récent en premier
			const nextDiscussions = list.sort((left, right) =>
				new Date(right.lastMessageAt || 0).getTime() - new Date(left.lastMessageAt || 0).getTime()
			);
			setDiscussions(nextDiscussions);

			const targetId = location.state?.selectDiscussionId;
			setSelectedDiscussion((currentSelected) => {
				if (!nextDiscussions.length) return null;
				// Auto-sélection depuis une notification
				if (targetId) {
					const target = nextDiscussions.find((item) => String(item._id) === String(targetId));
					if (target) return target;
				}
				if (!currentSelected) return nextDiscussions[0];
				// Garde la sélection en cours si elle existe toujours
				const refreshed = nextDiscussions.find((item) => item._id === currentSelected._id);
				return refreshed || nextDiscussions[0];
			});
		} catch (requestError) {
			setDiscussions([]);
			setError(requestError.message || 'Erreur de chargement des discussions.');
		} finally {
			setIsLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasUser, userId, location.state?.selectDiscussionId]);

	useEffect(() => { loadDiscussions(); }, [loadDiscussions]);

	// Marque les messages comme "lus" quand on sélectionne une discussion
	useEffect(() => {
		if (!selectedDiscussion || !authUser) return;
		const readKey = `ux-msg-read-at:${userId || String(authUser.email || 'guest').toLowerCase()}`;
		window.localStorage.setItem(readKey, String(Date.now()));
	}, [authUser, selectedDiscussion, userId]);

	/**
	 * Chargement de la conversation sélectionnée (messages complets).
	 * Utilise POST /api/discussions pour récupérer ou créer la conversation.
	 * La variable isCancelled évite les mises à jour d'état après démontage.
	 */
	useEffect(() => {
		if (!selectedDiscussion || !authUser) {
			setConversation(null);
			setConversationError('');
			return;
		}

		let isCancelled = false;
		const loadConversation = async () => {
			setConversationLoading(true);
			setConversationError('');
			try {
				const response = await fetch(`${apiBaseUrl}/api/discussions`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						conversationKey: selectedDiscussion.conversationKey,
						contextType: selectedDiscussion.contextType || 'agency',
						listingId: selectedDiscussion.listingId || '',
						vehicleName: selectedDiscussion.vehicleName || '',
						agencyId: selectedDiscussion.agencyId || null,
						agencyName: selectedDiscussion.agencyName || '',
						customerId: authUser.id,
						customerName: authUser.name
					})
				});
				const data = await response.json();
				if (!response.ok) throw new Error(data.message || 'Impossible de charger la discussion.');
				if (!isCancelled) setConversation(data);
			} catch (loadError) {
				if (!isCancelled) {
					setConversation(null);
					setConversationError(loadError.message || 'Impossible de charger la discussion.');
				}
			} finally {
				if (!isCancelled) setConversationLoading(false);
			}
		};

		loadConversation();
		return () => { isCancelled = true; };
	}, [authUser, refreshToken, selectedDiscussion]);

	// Auto-scroll vers le dernier message
	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [selectedMessages]);

	// Charge la photo de l'autre participant (agence ou client selon le rôle)
	useEffect(() => {
		setOtherUserPhoto('');
		if (!selectedDiscussion) return;
		const otherId = authUser?.role === 'agency'
			? selectedDiscussion.customerId
			: selectedDiscussion.agencyId;
		if (!otherId) return;
		fetch(`${apiBaseUrl}/api/users/${otherId}`)
			.then((r) => r.json())
			.then((data) => { if (data?.user?.profilePhotoUrl) setOtherUserPhoto(data.user.profilePhotoUrl); })
			.catch(() => {});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authUser?.role, selectedDiscussion?._id]);

	/**
	 * Sondage toutes les 6 secondes sur la conversation ouverte.
	 * Met à jour uniquement si de nouveaux messages sont arrivés.
	 */
	// Temps réel via Socket.io
	useEffect(() => {
		if (!selectedDiscussion?._id) return;
		const socket = getSocket();
		const discussionId = String(selectedDiscussion._id);

		const joinRoom = () => socket.emit('join_discussion', discussionId);

		// Rejoint immédiatement si déjà connecté, sinon attend la connexion
		if (socket.connected) {
			joinRoom();
		} else {
			socket.once('connect', joinRoom);
		}

		const handleNewMessage = (payload) => {
			if (String(payload?.discussionId) !== discussionId) return;
			const incoming = payload?.message;
			if (!incoming) return;
			setConversation((prev) => {
				if (!prev) return prev;
				const alreadyExists = prev.messages?.some((m) => String(m._id) === String(incoming._id));
				if (alreadyExists) return prev;
				return {
					...prev,
					messages: [...(prev.messages || []), incoming],
					lastMessagePreview: incoming.text?.slice(0, 120) || '',
					lastMessageAt: incoming.createdAt || new Date().toISOString(),
				};
			});
		};

		socket.on('new_message', handleNewMessage);

		return () => {
			socket.off('connect', joinRoom);
			socket.off('new_message', handleNewMessage);
			socket.emit('leave_discussion', discussionId);
		};
	}, [selectedDiscussion?._id]);

	const handleDeleteDiscussion = (id, e) => {
		e.stopPropagation();
		setOpenMenuId(null);
		showConfirm(
			'Supprimer cette discussion ?',
			'Cette conversation sera définitivement supprimée.',
			async () => {
				try {
					await fetch(`${apiBaseUrl}/api/discussions/${id}`, { method: 'DELETE' });
					setDiscussions((prev) => prev.filter((d) => d._id !== id));
					if (selectedDiscussion?._id === id) setSelectedDiscussion(null);
				} catch { /* ignore */ }
			}
		);
	};

	const handleDeleteAll = () => {
		showConfirm(
			'Supprimer toutes les discussions ?',
			'Toutes vos conversations seront définitivement supprimées.',
			async () => {
				setDeleteAllLoading(true);
				try {
					const param = authUser?.role === 'admin' ? 'all=1'
						: authUser?.role === 'agency' ? `agencyId=${encodeURIComponent(userId)}`
						: `customerId=${encodeURIComponent(userId)}`;
					await fetch(`${apiBaseUrl}/api/discussions?${param}`, { method: 'DELETE' });
					setDiscussions([]);
					setSelectedDiscussion(null);
				} catch { /* ignore */ }
				setDeleteAllLoading(false);
			}
		);
	};

	const handleOpenDiscussion = (discussion) => {
		setSelectedDiscussion(discussion);
		setListExpanded(false); // referme la liste mobile après sélection
	};

	/** Envoie un message dans la conversation ouverte */
	const handleSend = async () => {
		const messageText = String(draft || '').trim();
		if (!messageText || !conversation?._id || !authUser) return;
		setSending(true);
		setConversationError('');

		try {
			const response = await fetch(`${apiBaseUrl}/api/discussions/${conversation._id}/messages`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					text: messageText,
					senderRole: authUser.role === 'agency' ? 'agency' : selectedDiscussion?.contextType === 'owner' ? 'owner' : 'customer',
					senderName: authUser.name,
					senderId: authUser.id
				})
			});
			const data = await response.json();
			if (!response.ok) throw new Error(data.message || 'Envoi impossible.');

			setConversation(data);
			// Met aussi à jour la liste pour avoir le bon lastMessagePreview
			setDiscussions((previous) => {
				const next = previous.map((item) => (item._id === data._id ? { ...item, ...data } : item));
				return next.sort((left, right) => new Date(right.lastMessageAt || 0).getTime() - new Date(left.lastMessageAt || 0).getTime());
			});
			setSelectedDiscussion(data);
			setDraft('');
		} catch (sendError) {
			setConversationError(sendError.message || 'Envoi impossible.');
		} finally {
			setSending(false);
		}
	};

	// ── Valeurs dérivées pour l'affichage de la conversation ─────────────────
	const currentConversation  = conversation || selectedDiscussion;
	const currentMessages      = selectedMessages;
	const selectedContextType  = currentConversation?.contextType === 'owner' ? 'owner' : 'agency';
	const selectedTitle        = currentConversation?.vehicleName || currentConversation?.agencyName || 'Discussion';
	const selectedSubtitle     = currentConversation?.agencyName || 'Agence partenaire';
	const selectedInitials     = String(selectedSubtitle || 'DS').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'DS';
	const selectedSlug         = encodeURIComponent(String(currentConversation?.agencyName || currentConversation?._id || 'agency').trim().replace(/\s+/g, '-').toLowerCase());
	const selectedState        = {
		contextType: selectedContextType,
		listingId: currentConversation?.listingId || '',
		vehicleName: currentConversation?.vehicleName || 'Annonce',
		agencyName: currentConversation?.agencyName || 'Agence partenaire',
		agencyId: currentConversation?.agencyId || '',
		conversationKey: currentConversation?.conversationKey || ''
	};

	// Infos de l'utilisateur pour la navbar
	const isAdmin    = authUser?.role === 'admin' || authUser?.role === 'agency';
	const displayName = authUser ? (authUser.name || authUser.email || 'Mon profil') : '';
	const initials   = authUser?.initials || 'U';
	const avatarUrl  = authUser?.photoUrl || '';

	// Ferme le dropdown profil au clic extérieur ou à Echap
	useEffect(() => {
		if (!profileOpen) return;
		const onOutside = (e) => { if (!e.target.closest('.vx-profile-dropdown')) setProfileOpen(false); };
		const onEsc = (e) => { if (e.key === 'Escape') setProfileOpen(false); };
		window.addEventListener('mousedown', onOutside);
		window.addEventListener('keydown', onEsc);
		return () => { window.removeEventListener('mousedown', onOutside); window.removeEventListener('keydown', onEsc); };
	}, [profileOpen]);

	// Ferme le menu 3-dot d'une discussion au clic extérieur
	useEffect(() => {
		if (!openMenuId) return;
		const onOutside = (e) => { if (!e.target.closest('.ux-msg-card-menu-wrap')) setOpenMenuId(null); };
		window.addEventListener('mousedown', onOutside);
		return () => window.removeEventListener('mousedown', onOutside);
	}, [openMenuId]);

	// Redirige vers /login si pas connecté
	if (!authUser) return <Navigate to="/login" replace />;

	return (
		<div className="ux-page ux-msg-page">

			{/* ── Navbar complète avec notifications et menu profil ── */}
			<header className="vx-nav-wrap">
				<div className="vx-shell">
					<nav className="vx-nav ux-msg-nav">
						<Link to="/" className="vx-brand">
							<div className="vx-brand-mark">
								<img src="/assets/images/logo.png" alt="UltimateXDrive" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
							</div>
							<span className="vx-brand-name">UltimateXDrive</span>
						</Link>
						<ul className="vx-nav-links">
							<li><Link to="/search">Recherche</Link></li>
							<li><Link to="/search">Flotte</Link></li>
							<li><a href="#services">Services</a></li>
							<li><a href="#contact">À propos</a></li>
						</ul>
						<div className="vx-nav-cta">
							<NotificationCenter authUser={authUser} onNavigate={() => setProfileOpen(false)} />
							<div className={`vx-profile-dropdown ${profileOpen ? 'is-open' : ''}`}>
								<button
									type="button"
									className={`vx-profile-trigger${isAdmin ? ' is-admin' : ''}`}
									onClick={() => setProfileOpen((value) => !value)}
									aria-expanded={profileOpen}
									aria-haspopup="menu"
								>
									<span className="vx-user-avatar">
										{avatarUrl ? <img src={avatarUrl} alt="" aria-hidden="true" /> : initials}
									</span>
									<span className="vx-user-name">{displayName}</span>
									<svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="vx-profile-chevron" aria-hidden="true">
										<polyline points="3,5 7,9 11,5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
									</svg>
								</button>
								<div className="vx-profile-dropdown-menu" role="menu" aria-label="Menu profil">
									<Link to="/profile" className="vx-profile-dropdown-item" onClick={() => setProfileOpen(false)} role="menuitem">Profil</Link>
									{isAdmin && (
										<Link to={authUser?.role === 'admin' ? '/admin' : '/agence'} className="vx-profile-dropdown-item" onClick={() => setProfileOpen(false)} role="menuitem">
											Dashboard
										</Link>
									)}
									<button type="button" className="vx-profile-dropdown-item" onClick={() => {
										window.localStorage.removeItem('exp-auth-user');
										window.localStorage.removeItem('exp-user-profile');
										notifyAuthUserChanged();
										navigate('/login');
										setProfileOpen(false);
									}} role="menuitem">
										Déconnexion
									</button>
								</div>
							</div>
						</div>
					</nav>
				</div>
			</header>

			<main className="ux-main-content ux-msg-main">
				<section className={`ux-shell ux-msg-layout${selectedDiscussion ? ' has-selection' : ''}`} aria-label="Toutes les discussions utilisateur">

					{/* ── Liste des discussions (panneau gauche) ── */}
					<aside className="ux-msg-list-pane">
						{/* Header bureau + toggle mobile */}
						<div className="ux-msg-list-pane-head">
							<div>
								<span className="vx-section-kicker">
									<FiMessageCircle size={13} aria-hidden="true" />
									Discussions
								</span>
								{/* Toggle mobile : affiche le titre + nombre + chevron */}
								<button
									type="button"
									className="ux-msg-mobile-toggle"
									onClick={() => setListExpanded((v) => !v)}
									aria-expanded={listExpanded}
								>
									<h2 style={{ margin: 0 }}>Vos conversations</h2>
									<span className="ux-msg-mobile-count">{discussions.length}</span>
									{listExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
								</button>
							</div>
							<div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
								<button type="button" className="ux-msg-refresh" onClick={loadDiscussions} title="Actualiser">
									<FiRefreshCw size={15} />
									<span className="ux-msg-refresh-label">Actualiser</span>
								</button>
								{discussions.length > 0 && (
									<button
										type="button"
										className="ux-msg-delete-all"
										onClick={handleDeleteAll}
										disabled={deleteAllLoading}
										title="Supprimer toutes les discussions"
									>
										<FiTrash2 size={14} />
										<span className="ux-msg-refresh-label">{deleteAllLoading ? '...' : ''}</span>
									</button>
								)}
							</div>
						</div>

						<div className="ux-msg-list-meta">
							<span>{discussions.length} discussion(s)</span>
							<span>{isLoading ? 'Chargement' : 'Prêt'}</span>
						</div>

						{isLoading ? <p className="ux-msg-state">Chargement des discussions...</p> : null}
						{!isLoading && error ? <p className="ux-msg-state ux-msg-state-error">{error}</p> : null}
						{!isLoading && !error && discussions.length === 0 ? (
							<p className="ux-msg-state">Aucune discussion pour le moment.</p>
						) : null}

						{/* Sur mobile la liste est masquée par défaut, visible quand listExpanded */}
						<div className={`ux-msg-list${listExpanded ? ' is-mobile-expanded' : ''}`}>
							{discussions.map((discussion) => {
								const isActive = selectedDiscussion?._id === discussion._id;
								const menuOpen = openMenuId === discussion._id;
								return (
									<div key={discussion._id} className="ux-msg-card-wrap">
										<button
											type="button"
											className={`ux-msg-card${isActive ? ' is-active' : ''}`}
											onClick={() => handleOpenDiscussion(discussion)}
										>
											{/* Avatar coloré */}
											{(() => {
												const av = getAvatarStyle(discussion.vehicleName || discussion.agencyName);
												return (
													<div className="ux-msg-card-thumb" aria-hidden="true" style={{ background: av.bg, color: av.color }}>
														{discussion.vehicleImageUrl
															? <img src={discussion.vehicleImageUrl} alt="" />
															: (discussion.vehicleName || discussion.agencyName || 'D').charAt(0).toUpperCase()
														}
													</div>
												);
											})()}
											<div className="ux-msg-card-body">
												<div className="ux-msg-card-head">
													<strong>{discussion.vehicleName || discussion.agencyName || 'Discussion'}</strong>
													<span><FiClock size={12} /> {formatDateTime(discussion.lastMessageAt || discussion.updatedAt)}</span>
												</div>
												<p>{discussion.lastMessagePreview || 'Clique pour ouvrir la conversation.'}</p>
												<small>
													{discussion.contextType === 'owner' ? 'Mode proprietaire' : 'Mode agence'}
													{discussion.agencyName ? ` · ${discussion.agencyName}` : ''}
												</small>
											</div>
										</button>

										{/* Bouton 3-dot */}
										<div className="ux-msg-card-menu-wrap">
											<button
												type="button"
												className="ux-msg-card-menu-btn"
												onClick={(e) => { e.stopPropagation(); setOpenMenuId(menuOpen ? null : discussion._id); }}
												aria-label="Options"
											>
												<FiMoreVertical size={15} />
											</button>
											{menuOpen && (
												<div className="ux-msg-card-dropdown">
													<button
														type="button"
														className="ux-msg-card-dropdown-item is-danger"
														onClick={(e) => handleDeleteDiscussion(discussion._id, e)}
													>
														<FiTrash2 size={13} /> Supprimer
													</button>
												</div>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</aside>

					{/* ── Conversation sélectionnée (panneau droit) ── */}
					<article className="ux-chat-panel ux-msg-detail-pane">
						{selectedDiscussion ? (
							<>
								<div className="ux-chat-panel-head ux-msg-detail-head">
									<div>
										{/* Bouton retour mobile */}
										<button type="button" className="ux-msg-back-btn" onClick={() => setSelectedDiscussion(null)}>
											<FiArrowLeft size={14} /> Discussions
										</button>
										{/* Avatar + infos */}
										{(() => {
											const av = getAvatarStyle(selectedTitle);
											return (
												<div className="ux-msg-conv-avatar" style={{ background: av.bg, color: av.color }}>
													{selectedTitle.charAt(0).toUpperCase()}
												</div>
											);
										})()}
										<div className="ux-msg-conv-info">
											<span className="ux-msg-kicker">Conversation active</span>
											<h2>{selectedTitle}</h2>
											<p>{selectedSubtitle} · {selectedContextType === 'owner' ? 'Mode propriétaire' : 'Mode agence'}</p>
										</div>
									</div>
									<div className="ux-msg-detail-actions">
										<span className="ux-chat-status">
											{conversationLoading ? 'Chargement...' : `${currentMessages.length} message(s)`}
										</span>
										<button type="button" className="vx-btn vx-btn-ghost" onClick={() => setRefreshToken((value) => value + 1)}>
											<FiRefreshCw size={14} />
											Actualiser
										</button>
										<Link to={`/chat/${selectedSlug}`} state={selectedState} className="vx-btn vx-btn-ghost">
											<FiArrowLeft size={14} />
											Ouvrir en plein écran
										</Link>
									</div>
								</div>

								{/* Messages */}
								<div className="ux-chat-messages">
									{conversationLoading ? <p className="ux-msg-state">Chargement de la conversation...</p> : null}
									{!conversationLoading && conversationError ? <p className="ux-msg-state ux-msg-state-error">{conversationError}</p> : null}
									{!conversationLoading && !conversationError && currentMessages.length === 0 ? (
										<p className="ux-msg-state">Aucun message pour le moment.</p>
									) : null}
									{currentMessages.map((message) => {
										const isSystem = message.senderRole === 'system';
										const isMine = message.senderId && authUser.id
											? String(message.senderId) === String(authUser.id)
											: message.senderRole === (authUser.role === 'agency' ? 'agency' : 'customer');
										const bubbleClass = isSystem ? 'is-system' : isMine ? 'is-user' : 'is-agent';

										return (
											<div key={message._id || `${message.senderName}-${message.createdAt}-${message.text}`} className={`ux-chat-message-row ${bubbleClass}`}>
												{bubbleClass === 'is-agent' && (
													<div className="ux-chat-avatar ux-chat-avatar-agency" aria-hidden="true">
														{otherUserPhoto ? <img src={otherUserPhoto} alt="" /> : selectedInitials}
													</div>
												)}
												<div className={`ux-chat-message ${bubbleClass}`}>
													<div className="ux-chat-message-head">
														<strong>{message.senderName}</strong>
														<span>{message.createdAt ? formatDateTime(message.createdAt) : ''}</span>
													</div>
													<p>{message.text}</p>
													{Array.isArray(message.photoUrls) && message.photoUrls.length > 0 && (
														<div className="ux-chat-photo-grid">
															{message.photoUrls.map((url, i) => (
																<a key={i} href={url} target="_blank" rel="noopener noreferrer">
																	<img src={url} alt={`Photo ${i + 1}`} className="ux-chat-photo-thumb" />
																</a>
															))}
														</div>
													)}
												</div>
												{bubbleClass === 'is-user' && (
													<div className="ux-chat-avatar ux-chat-avatar-user" aria-hidden="true">
														{authUser.photoUrl ? <img src={authUser.photoUrl} alt="" /> : authUser.initials}
													</div>
												)}
											</div>
										);
									})}
									<div ref={bottomRef} />
								</div>

								{conversationError ? <p className="ux-chat-error">{conversationError}</p> : null}

								{/* ── Bouton "Je suis devant le véhicule" (utilisateur uniquement) ── */}
								{authUser?.role === 'user' && currentConversation?.vehicleName && (
									<div className="ux-chat-pickup-bar">
										{pickupPhotos.length === 0 ? (
											<div style={{ position: 'relative', display: 'inline-block' }}>
												<button type="button" className="ux-chat-pickup-btn" onClick={() => setPickupMenuOpen((v) => !v)}>
													<FiCamera size={15} />
													Je suis devant le véhicule
												</button>
												{pickupMenuOpen && (
													<div style={{
														position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
														background: '#fff', border: '1px solid #e5e7eb',
														borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
														padding: '0.9rem 1rem', minWidth: 220, zIndex: 200,
													}}>
														<p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', margin: '0 0 0.6rem', fontFamily: 'Syne, sans-serif' }}>
															Nombre de photos à prendre
														</p>
														<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.7rem' }}>
															{[1,2,3,4,5,6,8,10].map((n) => (
																<button key={n} type="button"
																	onClick={() => { setPickupMaxPhotos(n); }}
																	style={{
																		width: 36, height: 36, borderRadius: 8,
																		border: pickupMaxPhotos === n ? '2px solid #1a56f0' : '1px solid #e5e7eb',
																		background: pickupMaxPhotos === n ? '#eff4ff' : '#fff',
																		color: pickupMaxPhotos === n ? '#1a56f0' : '#374151',
																		fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem',
																	}}
																>{n}</button>
															))}
														</div>
														<button type="button"
															onClick={() => { setPickupMenuOpen(false); openPickupCam(); }}
															style={{
																width: '100%', padding: '0.55rem',
																background: 'linear-gradient(135deg, #1a56f0, #0f3fd4)',
																color: '#fff', border: 'none', borderRadius: 8,
																fontFamily: 'Syne, sans-serif', fontWeight: 700,
																fontSize: '0.82rem', cursor: 'pointer',
																display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
															}}
														>
															<FiCamera size={13} /> Ouvrir la caméra ({pickupMaxPhotos} photo{pickupMaxPhotos > 1 ? 's' : ''})
														</button>
													</div>
												)}
											</div>
										) : (
											<div className="ux-chat-pickup-confirm">
												<div className="ux-chat-pickup-thumbs">
													{pickupPhotos.map((p, i) => (
														<div key={i} className="ux-chat-pickup-thumb">
															<img src={p.previewUrl} alt={`Véhicule ${i+1}`} />
															<button type="button" onClick={() => {
																URL.revokeObjectURL(p.previewUrl);
																setPickupPhotos(prev => prev.filter((_, j) => j !== i));
															}}>
																<FiX size={10} />
															</button>
														</div>
													))}
													{pickupPhotos.length < pickupMaxPhotos && (
														<button type="button" className="ux-chat-pickup-add" onClick={openPickupCam} title="Prendre une autre photo">
															<FiCamera size={16} />
														</button>
													)}
												</div>
												<button
													type="button"
													className="ux-chat-pickup-send"
													onClick={confirmPickup}
													disabled={pickupSending}
												>
													<FiCheck size={14} />
													{pickupSending ? 'Envoi...' : 'Confirmer la prise en charge'}
												</button>
											</div>
										)}
										{pickupCamError && <p className="ux-chat-pickup-error">{pickupCamError}</p>}
									</div>
								)}

								{/* Compositeur de message */}
								<form
									className="ux-chat-composer"
									onSubmit={(event) => { event.preventDefault(); handleSend(); }}
								>
									<textarea
										value={draft}
										onChange={(event) => setDraft(event.target.value)}
										onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
										placeholder="Écrivez votre message… (Entrée pour envoyer)"
										rows="2"
									/>
									<button type="submit" className="ux-chat-send-btn" disabled={sending || !draft.trim()}>
										<FiSend size={15} />
										<span>{sending ? 'Envoi…' : 'Envoyer'}</span>
									</button>
								</form>
							</>
						) : (
							// État vide : aucune conversation sélectionnée
							<div className="ux-msg-detail-empty">
								<div className="ux-msg-detail-empty-mark">
									<FiMessageCircle size={18} />
								</div>
								<h2>Sélectionne une discussion</h2>
								<p>Choisis une conversation dans la colonne de gauche pour afficher les messages ici.</p>
							</div>
						)}
					</article>
				</section>
			</main>

			<BottomNav />

			{/* ── Modal caméra prise en charge ── */}
			{pickupCamOpen && (
				<div style={{
					position: 'fixed', inset: 0, zIndex: 9999,
					background: 'rgba(0,4,18,0.95)',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					backdropFilter: 'blur(10px)', padding: '1rem',
				}}>
					<div style={{
						width: '100%', maxWidth: 480,
						background: '#0a0f1e', borderRadius: 20, overflow: 'hidden',
						boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
						border: '1px solid rgba(255,255,255,0.07)',
					}}>
						<div style={{
							padding: '0.9rem 1.2rem', borderBottom: '1px solid rgba(255,255,255,0.07)',
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
						}}>
							<div>
								<span style={{ color: '#e8f0ff', fontWeight: 700, fontFamily: 'Syne', fontSize: '0.9rem' }}>
									📸 Photo du véhicule
								</span>
								<p style={{ fontFamily: 'DM Mono', fontSize: '0.62rem', color: '#5a6a9a', margin: '2px 0 0', letterSpacing: '0.05em' }}>
									{pickupPhotos.length}/{pickupMaxPhotos} — photographiez l'état du véhicule
								</p>
							</div>
							<button onClick={closePickupCam} style={{
								background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
								borderRadius: 8, width: 34, height: 34, cursor: 'pointer', color: '#c0cce8',
								display: 'flex', alignItems: 'center', justifyContent: 'center',
							}}>
								<FiX size={16} />
							</button>
						</div>

						<div style={{ position: 'relative', background: '#000', lineHeight: 0 }}>
							<video
								ref={pickupVideoRef}
								autoPlay playsInline muted
								style={{ width: '100%', display: 'block', maxHeight: 340, objectFit: 'cover' }}
							/>
							<div style={{
								position: 'absolute', bottom: 14, left: 0, right: 0,
								display: 'flex', justifyContent: 'center', pointerEvents: 'none',
							}}>
								<span style={{
									background: 'rgba(10,26,79,0.85)', color: '#fff',
									fontFamily: 'DM Mono', fontSize: '0.75rem', fontWeight: 600,
									padding: '5px 18px', borderRadius: 20, letterSpacing: '0.02em',
								}}>
									Photographiez l'état du véhicule
								</span>
							</div>
						</div>

						<div style={{
							padding: '1rem 1.2rem', borderTop: '1px solid rgba(255,255,255,0.07)',
							display: 'flex', gap: '0.7rem', justifyContent: 'flex-end',
						}}>
							<button onClick={closePickupCam} style={{
								fontFamily: 'Syne', padding: '0.62rem 1.1rem',
								background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
								borderRadius: 8, color: '#c0cce8', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
							}}>Annuler</button>
							<button onClick={capturePickupPhoto} style={{
								fontFamily: 'Syne', padding: '0.62rem 1.4rem',
								background: 'linear-gradient(135deg,#1a56f0,#0f3fd4)',
								border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer',
								fontSize: '0.85rem', fontWeight: 700,
								display: 'flex', alignItems: 'center', gap: 7,
								boxShadow: '0 4px 16px rgba(26,86,240,0.45)',
							}}>
								<FiCamera size={15} /> Capturer
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ── Modale de confirmation ── */}
			{confirmDialog.open && (
				<div className="ux-confirm-overlay" onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}>
					<div className="ux-confirm-modal" onClick={e => e.stopPropagation()}>
						<div className="ux-confirm-icon">
							<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<polyline points="3 6 5 6 21 6" />
								<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
								<path d="M10 11v6M14 11v6" />
								<path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
							</svg>
						</div>
						<h3 className="ux-confirm-title">{confirmDialog.message}</h3>
						{confirmDialog.subtext && <p className="ux-confirm-sub">{confirmDialog.subtext}</p>}
						<div className="ux-confirm-actions">
							<button
								type="button"
								className="ux-confirm-cancel"
								onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}
							>
								Annuler
							</button>
							<button
								type="button"
								className="ux-confirm-ok"
								onClick={() => {
									setConfirmDialog(d => ({ ...d, open: false }));
									confirmDialog.onConfirm && confirmDialog.onConfirm();
								}}
							>
								Supprimer
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default DiscusionInterface;
